import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Article, ArticleContent, ArticleReference } from '@/types'

import Link from 'next/link'

import {
    ArrowLeft,
    CircleCheck,
    Copy,
    ExternalLink,
    FileArchive,
    FileSpreadsheet,
    FileText,
    FileVideo,
    FolderKanban,
    Info,
    Lightbulb,
    Presentation,
    TrendingUp,
    TriangleAlert
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'
import { copyText } from '@/lib/clipboard'
import { useToast } from '@/components/generals/providers'
import { ConfluenceHint } from '@/components/generals/confluence-hint'
import { Accordion } from '@/components/ds/accordion'
import { Badge } from '@/components/ds/badge'
import { Callout } from '@/components/ds/feedback'
import { FlowDiagram } from '@/components/knowledge/flow-diagram'

export type TocEntry = {
    id: string
    title: string
}

export const buildToc = (article: Article, content: ArticleContent): TocEntry[] => {
    const entries: TocEntry[] = [{ id: 'summary', title: 'خلاصه مدیریتی' }]

    if (article.lesson) entries.push({ id: 'experience', title: 'خلاصه تجربه' })

    entries.push({ id: 'goal', title: 'هدف و دامنه کاربرد' })

    if (content.prerequisites.length) entries.push({ id: 'prerequisites', title: 'پیش‌نیازها' })
    if (content.sections.length) entries.push({ id: 'details', title: 'شرح دانش' })
    if (content.steps.length) entries.push({ id: 'steps', title: 'مراحل اجرا' })
    if (content.diagram) entries.push({ id: 'diagrams', title: 'تصاویر و نمودارها' })
    if (content.tips.length) entries.push({ id: 'tips', title: 'نکات تخصصی' })
    if (content.pitfalls.length) entries.push({ id: 'pitfalls', title: 'خطاهای رایج و راهکارها' })
    if (content.recommendations?.length) entries.push({ id: 'recommendations', title: 'توصیه‌ها' })
    if (content.references.length) entries.push({ id: 'references', title: 'مستندات و منابع مرتبط' })

    return entries
}

const REFERENCE_KINDS: Record<ArticleReference['kind'], { icon: LucideIcon, label: string }> = {
    pdf: { icon: FileText, label: 'PDF' },
    docx: { icon: FileText, label: 'Word' },
    xlsx: { icon: FileSpreadsheet, label: 'Excel' },
    pptx: { icon: Presentation, label: 'PowerPoint' },
    zip: { icon: FileArchive, label: 'ZIP' },
    mp4: { icon: FileVideo, label: 'ویدئو MP4' },
    page: { icon: FileText, label: 'صفحه دانش' },
    link: { icon: ExternalLink, label: 'سامانه داخلی' }
}

interface ArticleSectionProps {
    id: string
    title: string
    hint?: string
    children: ReactNode
}

const ArticleSection: FC<ArticleSectionProps> = ({ id, title, hint, children }): ReactNode => {
    return (
        <section id={id} aria-labelledby={`${id}-title`} className='relative flex scroll-mt-24 flex-col gap-4'>
            {hint && <ConfluenceHint id={hint} />}

            <h2 id={`${id}-title`} className='flex items-center gap-2.5 text-h2 text-navy'>
                <span className='h-6 w-1 shrink-0 rounded-full bg-primary' aria-hidden='true' />
                {title}
            </h2>

            {children}
        </section>
    )
}

interface CodeBlockProps {
    language: string
    content: string
}

export const CodeBlock: FC<CodeBlockProps> = ({ language, content }): ReactNode => {
    const { notify } = useToast()

    return (
        <div className='overflow-hidden rounded-lg border border-navy/20 bg-navy'>
            <div className='flex items-center justify-between border-b border-white/10 px-4 py-2'>
                <span className='font-mono text-caption uppercase tracking-wide text-white/60' dir='ltr'>{language}</span>

                <button
                    type='button'
                    onClick={async () => {
                        const copied = await copyText(content)

                        notify(copied ? 'نمونه کد کپی شد' : 'کپی نمونه کد ممکن نشد', copied ? 'success' : 'error')
                    }}
                    className='flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2 text-caption text-white/70 transition-colors hover:bg-white/10 hover:text-white'
                >
                    <Copy className='size-3.5' aria-hidden='true' />
                    کپی
                </button>
            </div>

            <pre dir='ltr' className='overflow-x-auto p-4 text-start font-mono text-[13px] leading-6 text-white/90'>
                <code>{content}</code>
            </pre>
        </div>
    )
}

const LESSON_PARTS: { key: 'challenge' | 'solution' | 'outcome', title: string, icon: LucideIcon, tone: string }[] = [
    { key: 'challenge', title: 'مسئله یا چالش', icon: TriangleAlert, tone: 'text-warning-strong' },
    { key: 'solution', title: 'راهکار ارائه‌شده', icon: Lightbulb, tone: 'text-primary-strong' },
    { key: 'outcome', title: 'نتیجه و دستاورد', icon: TrendingUp, tone: 'text-success-strong' }
]

interface ArticleBodyProps {
    article: Article
    content: ArticleContent
}

export const ArticleBody: FC<ArticleBodyProps> = ({ article, content }): ReactNode => {
    return (
        <div className='flex flex-col gap-10'>
            <ArticleSection id='summary' title='خلاصه مدیریتی' hint='article-summary'>
                <div className='flex gap-3 rounded-lg border border-primary/25 bg-primary-soft/70 p-4 sm:p-5'>
                    <Info className='mt-1.5 size-5 shrink-0 text-primary-strong' aria-hidden='true' />
                    <p className='text-[1rem] leading-8 text-foreground'>{article.summary}</p>
                </div>
            </ArticleSection>

            {article.lesson && (
                <ArticleSection id='experience' title='خلاصه تجربه'>
                    <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <FolderKanban className='size-4 text-primary' aria-hidden='true' />
                        پروژه یا واحد مرتبط:
                        <span className='font-semibold text-navy'>{article.lesson.project}</span>
                    </p>

                    <dl className='grid gap-3 md:grid-cols-3'>
                        {LESSON_PARTS.map(part => {
                            const Icon = part.icon

                            return (
                                <div key={part.key} className='flex flex-col gap-1.5 rounded-lg border border-border bg-secondary/50 p-4'>
                                    <dt className={cn('flex items-center gap-1.5 text-small font-bold', part.tone)}>
                                        <Icon className='size-4' aria-hidden='true' />
                                        {part.title}
                                    </dt>

                                    <dd className='text-body text-foreground'>{article.lesson?.[part.key]}</dd>
                                </div>
                            )
                        })}
                    </dl>
                </ArticleSection>
            )}

            <ArticleSection id='goal' title='هدف و دامنه کاربرد'>
                <p className='text-[1rem] leading-8 text-foreground'>{content.goal}</p>

                <div className='rounded-lg border border-border bg-secondary/50 p-4'>
                    <p className='text-small font-bold text-navy'>دامنه کاربرد</p>
                    <p className='mt-1 text-body text-foreground'>{content.scope}</p>
                </div>
            </ArticleSection>

            {content.prerequisites.length > 0 && (
                <ArticleSection id='prerequisites' title='پیش‌نیازها'>
                    <ul className='grid gap-2 sm:grid-cols-2'>
                        {content.prerequisites.map(item => (
                            <li key={item} className='flex items-start gap-2.5 rounded-lg border border-border px-3.5 py-3 text-body text-foreground'>
                                <CircleCheck className='mt-1 size-4 shrink-0 text-success' aria-hidden='true' />
                                {item}
                            </li>
                        ))}
                    </ul>
                </ArticleSection>
            )}

            {content.sections.length > 0 && (
                <ArticleSection id='details' title='شرح دانش'>
                    <div className='flex flex-col gap-7'>
                        {content.sections.map(section => (
                            <div key={section.title} className='flex flex-col gap-3'>
                                <h3 className='text-h3 text-navy'>{section.title}</h3>

                                {section.paragraphs.map(paragraph => (
                                    <p key={paragraph} className='text-[1rem] leading-8 text-foreground'>{paragraph}</p>
                                ))}

                                {section.bullets && (
                                    <ul className='flex flex-col gap-2'>
                                        {section.bullets.map(bullet => (
                                            <li key={bullet} className='flex gap-3 text-[1rem] leading-8 text-foreground'>
                                                <span className='mt-3.5 size-1.5 shrink-0 rounded-full bg-primary' aria-hidden='true' />
                                                <span>{bullet}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {section.code && <CodeBlock language={section.code.language} content={section.code.content} />}
                            </div>
                        ))}
                    </div>
                </ArticleSection>
            )}

            {content.steps.length > 0 && (
                <ArticleSection id='steps' title='مراحل اجرا' hint='article-steps'>
                    <ol className='flex flex-col'>
                        {content.steps.map((step, index) => (
                            <li key={step.title} className='relative flex gap-4 pb-6 last:pb-0'>
                                {index < content.steps.length - 1 && (
                                    <span className='absolute bottom-1 start-[15px] top-10 w-px bg-border' aria-hidden='true' />
                                )}

                                <span className='relative flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-card text-sm font-bold text-primary-deep'>
                                    {toFaDigits(index + 1)}
                                </span>

                                <div className='flex flex-col gap-1 pt-0.5'>
                                    <p className='text-body font-bold text-navy'>{step.title}</p>
                                    <p className='text-body text-muted-foreground'>{step.description}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </ArticleSection>
            )}

            {content.diagram && (
                <ArticleSection id='diagrams' title='تصاویر و نمودارها' hint='article-diagram'>
                    <FlowDiagram diagram={content.diagram} />
                </ArticleSection>
            )}

            {content.tips.length > 0 && (
                <ArticleSection id='tips' title='نکات تخصصی' hint='article-tips'>
                    <div className='flex flex-col gap-3'>
                        {content.tips.map(tip => (
                            <Callout key={tip} tone='tip'>
                                <p>{tip}</p>
                            </Callout>
                        ))}
                    </div>
                </ArticleSection>
            )}

            {content.pitfalls.length > 0 && (
                <ArticleSection id='pitfalls' title='خطاهای رایج و راهکارها' hint='article-pitfalls'>
                    <Accordion
                        multiple
                        defaultOpen={[`${article.slug}-pitfall-0`]}
                        items={content.pitfalls.map((pitfall, index) => ({
                            id: `${article.slug}-pitfall-${index}`,
                            title: (
                                <span className='flex items-start gap-2.5'>
                                    <TriangleAlert className='mt-1 size-4 shrink-0 text-warning' aria-hidden='true' />
                                    {pitfall.problem}
                                </span>
                            ),
                            content: (
                                <p className='flex gap-2 text-foreground'>
                                    <span className='shrink-0 font-bold text-success-strong'>راهکار:</span>
                                    <span>{pitfall.solution}</span>
                                </p>
                            )
                        }))}
                    />
                </ArticleSection>
            )}

            {content.recommendations && content.recommendations.length > 0 && (
                <ArticleSection id='recommendations' title='توصیه‌ها'>
                    <ul className='flex flex-col gap-2.5'>
                        {content.recommendations.map(item => (
                            <li key={item} className='flex items-start gap-3 text-[1rem] leading-8 text-foreground'>
                                <CircleCheck className='mt-2 size-4 shrink-0 text-primary' aria-hidden='true' />
                                {item}
                            </li>
                        ))}
                    </ul>
                </ArticleSection>
            )}

            {content.references.length > 0 && (
                <ArticleSection id='references' title='مستندات و منابع مرتبط' hint='article-references'>
                    <ul className='flex flex-col divide-y divide-border rounded-lg border border-border'>
                        {content.references.map(reference => {
                            const kind = REFERENCE_KINDS[reference.kind]
                            const Icon = kind.icon
                            const isPage = reference.kind === 'page' && reference.slug

                            return (
                                <li key={reference.title} className='flex items-center gap-3 px-4 py-3'>
                                    <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', isPage ? 'bg-primary-soft text-primary' : 'bg-secondary text-navy')}>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <div className='flex min-w-0 flex-1 flex-col'>
                                        <p className='truncate text-sm font-semibold text-navy'>{reference.title}</p>
                                        <p className='text-caption text-muted-foreground'>
                                            {kind.label}
                                            {reference.size && <> · {reference.size}</>}
                                        </p>
                                    </div>

                                    {isPage ? (
                                        <Link
                                            href={`/knowledge/${reference.slug}`}
                                            className='group inline-flex shrink-0 items-center gap-1 text-small font-semibold text-primary-strong transition-colors hover:text-primary-deep'
                                        >
                                            مشاهده
                                            <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
                                        </Link>
                                    ) : (
                                        <Badge tone='outline'>{reference.kind === 'link' ? 'پیوند' : 'پیوست'}</Badge>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </ArticleSection>
            )}
        </div>
    )
}
